import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { limitConcurrentRequests } from 'src/constants/limits';
import { Suggestion } from 'src/containers/ProjectMapping/types';
import { Connection } from 'src/store/connections/types';
import { ProjectResource } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { JsonLdResult } from 'src/templates/queries/types';
import { parseSuggestionResults } from 'src/utils/jsonld/parseSuggestionResults';
import { createSuggestionsRequestContents } from 'src/utils/suggestions/createSuggestionsRequestContents';
import { getClassIrisForSuggestionsRequest } from 'src/utils/suggestions/getClassIrisForSuggestionsRequest';

/**
 * Requests suggestions for resources by connecting to the stardog server
 * that each resource belongs to.
 */
export const requestSuggestions =
  ({
    classIris,
    connection,
    projectId,
    resourceIds,
  }: {
    classIris: string[];
    connection: Connection;
    projectId: string;
    resourceIds: string[];
  }) =>
  async (_: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();

    const project = state.projects.projectsById[projectId];
    if (!project) {
      console.warn('!project');
      return null;
    }

    const {
      dataSources: { typeDescriptionByConnectionId },
      features: { stardogByConnectionId },
    } = getState();
    const classIrisForSuggestionsRequest = getClassIrisForSuggestionsRequest(
      classIris,
      project.diagram.model
    );

    const requestContents = createSuggestionsRequestContents({
      classIris: classIrisForSuggestionsRequest,
      defaultConnectionId: connection.id,
      model: project.diagram.model,
      project,
      resourceIds,
      stardogFeaturesByConnectionId: stardogByConnectionId,
      typeDescriptionByConnectionId,
    });

    const suggestions: Suggestion[] = [];

    await Promise.all(
      requestContents.map(
        async ({
          connectionId,
          content,
          resource: requestContentResource,
          tableNameToResourceMap,
        }) =>
          limitConcurrentRequests(async () => {
            const stardogFeatures = connectionId
              ? stardogByConnectionId[connectionId]
              : null;

            const response =
              await dataSourcesStardogRequestDispatchers.requestSuggestions({
                connection,
                content,
              });

            if (
              response.type ===
              DataSourcesActionType.REQUEST_SUGGESTIONS_SUCCESS
            ) {
              const body: JsonLdResult[] = response.payload.response?.body;
              const suggestionsWithoutResource = parseSuggestionResults(
                body,
                classIris.length ? project.diagram.model : null,
                stardogFeatures?.isDataSourceRelationshipSuggestionsSupported
                  ? false
                  : resourceIds.length > 1 && !classIris.length
              );
              suggestionsWithoutResource.forEach((suggestion) => {
                let resource: ProjectResource | undefined;
                if (requestContentResource) {
                  resource = requestContentResource;
                } else if (suggestion.source && tableNameToResourceMap) {
                  resource = tableNameToResourceMap[suggestion.source];
                }
                if (resource) {
                  suggestions.push({ ...suggestion, resource });
                } else {
                  console.warn(`couldn't match a suggestion to a resource`, {
                    suggestion,
                    requestContentResource,
                    tableNameToResourceMap,
                  });
                }
              });
            }
          })
      )
    );

    return suggestions;
  };
